import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingCart,
  Heart,
  Search,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  Filter,
  Star,
  Award,
  Users,
  Shield,
} from "lucide-react";
import kenteHero from "@/assets/kente-hero.jpg";

interface StoreItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  images: string[];
  isPublished: boolean;
  stock: number;
  tags: string[];
}

interface StoreData {
  name: string;
  description: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  items: StoreItem[];
  isPublished: boolean;
  storeUrl: string;
  contactInfo?: {
    email: string;
    phone: string;
    location: string;
    businessHours: {
      weekdays: string;
      saturday: string;
      sunday: string;
    };
  };
}

interface CustomerStoreProps {
  storeData: StoreData;
  storeSlug: string;
}

const CustomerStore = ({ storeData, storeSlug }: CustomerStoreProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cart, setCart] = useState<StoreItem[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const categories = [
    "all",
    ...new Set(storeData.items.map((item) => item.category)),
  ];

  const filteredItems = storeData.items.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || item.category === selectedCategory;
    return matchesSearch && matchesCategory && item.isPublished;
  });

  const addToCart = (item: StoreItem) => {
    setCart((prev) => [...prev, item]);
  };

  const removeFromCart = (itemId: string) => {
    setCart((prev) => prev.filter((item) => item.id !== itemId));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price, 0);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className="sticky top-0 z-50 shadow-sm"
        style={{ backgroundColor: storeData.primaryColor, color: "white" }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              {storeData.logo && (
                <img
                  src={storeData.logo}
                  alt="Store Logo"
                  className="h-10 w-10 object-contain bg-white rounded p-1"
                />
              )}
              <div>
                <h1 className="text-xl font-bold">{storeData.name}</h1>
                <p className="text-sm opacity-90 hidden sm:block">
                  {storeData.description}
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#home" className="hover:underline">
                Home
              </a>
              <a href="#about" className="hover:underline">
                About
              </a>
              <a href="#store" className="hover:underline">
                Store
              </a>
              <a href="#contact" className="hover:underline">
                Contact
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden text-white hover:bg-white/10"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden pb-4 border-t border-white/20">
              <div className="flex flex-col gap-3 pt-4">
                <a href="#home" className="hover:underline">
                  Home
                </a>
                <a href="#about" className="hover:underline">
                  About
                </a>
                <a href="#store" className="hover:underline">
                  Store
                </a>
                <a href="#contact" className="hover:underline">
                  Contact
                </a>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
      >
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={kenteHero}
            alt="Traditional Kente cloth background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="mb-6">
              {storeData.logo && (
                <img
                  src={storeData.logo}
                  alt="Store Logo"
                  className="h-20 w-20 object-contain bg-white rounded-full p-2 mx-auto mb-6"
                />
              )}
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Welcome to {storeData.name}
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
              {storeData.description ||
                "Discover authentic Kente designs that celebrate our rich cultural heritage. Each piece is carefully crafted with traditional techniques passed down through generations."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
                onClick={() =>
                  document
                    .getElementById("store")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                <ShoppingCart className="h-6 w-6 mr-2" />
                Shop Our Collection
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-semibold"
                onClick={() =>
                  document
                    .getElementById("about")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                <Award className="h-6 w-6 mr-2" />
                Learn Our Story
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Store Section */}
      <section id="store" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2
              className="text-4xl font-bold mb-6"
              style={{ color: storeData.primaryColor }}
            >
              Our Kente Collection
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover authentic Kente designs crafted by master weavers. Each
              piece tells a story and celebrates our rich cultural heritage.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search for Kente designs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 text-lg"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={
                      selectedCategory === category ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className="h-12 px-6"
                    style={
                      selectedCategory === category
                        ? {
                            backgroundColor: storeData.primaryColor,
                            color: "white",
                            border: "none",
                          }
                        : {}
                    }
                  >
                    {category === "all" ? "All Products" : category}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Products Grid */}
          {filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center bg-gray-100">
                <Search className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-700 mb-2">
                No products found
              </h3>
              <p className="text-gray-500 text-lg">
                Try adjusting your search or filters to find what you're looking
                for
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredItems.map((item) => (
                <Card
                  key={item.id}
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
                >
                  <div className="aspect-square bg-gray-100 relative group">
                    {item.images && item.images.length > 0 ? (
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div
                            className="w-20 h-20 mx-auto mb-3 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: storeData.accentColor }}
                          >
                            <span className="text-3xl">🧵</span>
                          </div>
                          <p className="text-sm text-gray-500 font-medium">
                            Kente Design
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />

                    {/* Wishlist button */}
                    <div className="absolute top-3 right-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-10 w-10 p-0 bg-white/90 hover:bg-white shadow-md"
                      >
                        <Heart className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Stock badges */}
                    {item.stock <= 5 && item.stock > 0 && (
                      <div className="absolute top-3 left-3">
                        <Badge
                          variant="destructive"
                          className="text-xs font-semibold"
                        >
                          Only {item.stock} left
                        </Badge>
                      </div>
                    )}
                    {item.stock === 0 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Badge
                          variant="secondary"
                          className="text-sm font-semibold bg-white text-gray-900"
                        >
                          Out of Stock
                        </Badge>
                      </div>
                    )}
                  </div>

                  <CardContent className="p-6">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge
                          variant="outline"
                          className="text-xs font-medium"
                        >
                          {item.category}
                        </Badge>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className="h-3 w-3 fill-yellow-400 text-yellow-400"
                            />
                          ))}
                        </div>
                      </div>

                      <h4 className="font-semibold text-xl leading-tight">
                        {item.name}
                      </h4>
                      <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                        {item.description}
                      </p>

                      <div className="flex items-center justify-between pt-2">
                        <div>
                          <span
                            className="font-bold text-2xl"
                            style={{ color: storeData.accentColor }}
                          >
                            ₵{item.price}
                          </span>
                          {item.stock > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {item.stock} in stock
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          disabled={item.stock === 0}
                          onClick={() => addToCart(item)}
                          className="px-6 py-2 font-semibold"
                          style={{
                            backgroundColor: storeData.primaryColor,
                            color: "white",
                            border: "none",
                          }}
                        >
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          {item.stock === 0 ? "Out of Stock" : "Add to Cart"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Main About Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2
                  className="text-4xl font-bold mb-6"
                  style={{ color: storeData.primaryColor }}
                >
                  Our Story
                </h2>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  Kente is more than just a fabric; it's a symbol of our rich
                  cultural heritage. Each pattern tells a story, and every color
                  has meaning. Our master weavers use traditional techniques
                  passed down through generations to create these beautiful
                  pieces.
                </p>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  Founded with a passion for preserving and sharing Ghana's
                  textile traditions, we work directly with skilled artisans to
                  bring you authentic Kente designs that honor our ancestors
                  while embracing modern craftsmanship.
                </p>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div
                      className="text-3xl font-bold"
                      style={{ color: storeData.accentColor }}
                    >
                      15+
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Years Experience
                    </div>
                  </div>
                  <div className="text-center">
                    <div
                      className="text-3xl font-bold"
                      style={{ color: storeData.accentColor }}
                    >
                      500+
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Happy Customers
                    </div>
                  </div>
                  <div className="text-center">
                    <div
                      className="text-3xl font-bold"
                      style={{ color: storeData.accentColor }}
                    >
                      100%
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Authentic
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center">
                  <div className="text-center">
                    <div
                      className="w-32 h-32 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: storeData.accentColor }}
                    >
                      <span className="text-6xl">🧵</span>
                    </div>
                    <p
                      className="text-lg font-semibold"
                      style={{ color: storeData.primaryColor }}
                    >
                      Handcrafted with Love
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow">
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: storeData.accentColor }}
                >
                  <Award
                    className="h-8 w-8"
                    style={{ color: storeData.primaryColor }}
                  />
                </div>
                <h4 className="font-semibold text-xl mb-3">
                  Authentic Designs
                </h4>
                <p className="text-muted-foreground">
                  Traditional patterns with deep cultural significance, each
                  telling a unique story
                </p>
              </div>
              <div className="text-center p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow">
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: storeData.accentColor }}
                >
                  <Users
                    className="h-8 w-8"
                    style={{ color: storeData.primaryColor }}
                  />
                </div>
                <h4 className="font-semibold text-xl mb-3">Master Weavers</h4>
                <p className="text-muted-foreground">
                  Skilled artisans with decades of experience in traditional
                  weaving techniques
                </p>
              </div>
              <div className="text-center p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow">
                <div
                  className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: storeData.accentColor }}
                >
                  <Shield
                    className="h-8 w-8"
                    style={{ color: storeData.primaryColor }}
                  />
                </div>
                <h4 className="font-semibold text-xl mb-3">
                  Quality Guaranteed
                </h4>
                <p className="text-muted-foreground">
                  Premium materials and meticulous craftsmanship in every piece
                  we create
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2
                className="text-4xl font-bold mb-6"
                style={{ color: storeData.primaryColor }}
              >
                Get in Touch
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Have questions about our Kente designs? We'd love to hear from
                you. Reach out to us for custom orders, wholesale inquiries, or
                any questions.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h3
                    className="text-2xl font-semibold mb-6"
                    style={{ color: storeData.primaryColor }}
                  >
                    Contact Information
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: storeData.accentColor }}
                      >
                        <Mail
                          className="h-6 w-6"
                          style={{ color: storeData.primaryColor }}
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg mb-1">Email</h4>
                        <p className="text-muted-foreground">
                          {storeData.contactInfo?.email ||
                            "info@kentestore.com"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          We respond within 24 hours
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: storeData.accentColor }}
                      >
                        <Phone
                          className="h-6 w-6"
                          style={{ color: storeData.primaryColor }}
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg mb-1">Phone</h4>
                        <p className="text-muted-foreground">
                          {storeData.contactInfo?.phone || "+233 24 123 4567"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Mon-Fri, 9AM-6PM GMT
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: storeData.accentColor }}
                      >
                        <MapPin
                          className="h-6 w-6"
                          style={{ color: storeData.primaryColor }}
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg mb-1">Location</h4>
                        <p className="text-muted-foreground">
                          {storeData.contactInfo?.location || "Accra, Ghana"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Visit our workshop by appointment
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="bg-white p-6 rounded-xl border border-gray-100">
                  <h4
                    className="font-semibold text-lg mb-4"
                    style={{ color: storeData.primaryColor }}
                  >
                    Business Hours
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Monday - Friday
                      </span>
                      <span className="font-medium">
                        {storeData.contactInfo?.businessHours?.weekdays ||
                          "9:00 AM - 6:00 PM"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Saturday</span>
                      <span className="font-medium">
                        {storeData.contactInfo?.businessHours?.saturday ||
                          "10:00 AM - 4:00 PM"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sunday</span>
                      <span className="font-medium">
                        {storeData.contactInfo?.businessHours?.sunday ||
                          "Closed"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white p-8 rounded-xl border border-gray-100">
                <h3
                  className="text-2xl font-semibold mb-6"
                  style={{ color: storeData.primaryColor }}
                >
                  Send us a Message
                </h3>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        First Name
                      </label>
                      <Input placeholder="Your first name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Last Name
                      </label>
                      <Input placeholder="Your last name" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Email
                    </label>
                    <Input type="email" placeholder="your.email@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Subject
                    </label>
                    <Input placeholder="What can we help you with?" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      className="w-full p-3 border border-gray-300 rounded-lg resize-none min-h-[120px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Tell us about your inquiry..."
                    />
                  </div>
                  <Button
                    className="w-full"
                    style={{
                      backgroundColor: storeData.primaryColor,
                      color: "white",
                      border: "none",
                    }}
                  >
                    Send Message
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-12 border-t"
        style={{ backgroundColor: storeData.primaryColor, color: "white" }}
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Store Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                {storeData.logo && (
                  <img
                    src={storeData.logo}
                    alt="Store Logo"
                    className="h-12 w-12 object-contain bg-white rounded-full p-2"
                  />
                )}
                <div>
                  <h3 className="text-xl font-bold">{storeData.name}</h3>
                  <p className="text-sm opacity-90">Authentic Kente Designs</p>
                </div>
              </div>
              <p className="text-sm opacity-90 mb-4 max-w-md">
                {storeData.description ||
                  "Discover authentic Kente designs that celebrate our rich cultural heritage. Each piece is carefully crafted with traditional techniques."}
              </p>
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-white hover:bg-white hover:text-gray-900"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-white hover:bg-white hover:text-gray-900"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call
                </Button>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <a
                  href="#home"
                  className="block text-sm opacity-90 hover:opacity-100 transition-opacity"
                >
                  Home
                </a>
                <a
                  href="#about"
                  className="block text-sm opacity-90 hover:opacity-100 transition-opacity"
                >
                  About
                </a>
                <a
                  href="#store"
                  className="block text-sm opacity-90 hover:opacity-100 transition-opacity"
                >
                  Store
                </a>
                <a
                  href="#contact"
                  className="block text-sm opacity-90 hover:opacity-100 transition-opacity"
                >
                  Contact
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold mb-4">Contact Info</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 opacity-70" />
                  <span className="opacity-90">
                    {storeData.contactInfo?.email || "info@kentestore.com"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 opacity-70" />
                  <span className="opacity-90">
                    {storeData.contactInfo?.phone || "+233 24 123 4567"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 opacity-70" />
                  <span className="opacity-90">
                    {storeData.contactInfo?.location || "Accra, Ghana"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/20 pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-sm opacity-90 mb-4 md:mb-0">
                © 2025 {storeData.name}. All rights reserved.
              </div>
              <div className="flex items-center gap-6 text-sm opacity-90">
                <a href="#" className="hover:opacity-100 transition-opacity">
                  Privacy Policy
                </a>
                <a href="#" className="hover:opacity-100 transition-opacity">
                  Terms of Service
                </a>
                <span className="opacity-70">
                  Powered by Kente Weave Design
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Shopping Cart Sidebar */}
      {cart.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <Card className="shadow-2xl border-0">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <ShoppingCart className="h-6 w-6" />
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                      {cart.length}
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{cart.length} items</p>
                    <p className="text-xs text-muted-foreground">
                      in your cart
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="text-xl font-bold"
                    style={{ color: storeData.accentColor }}
                  >
                    ₵{getTotalPrice()}
                  </div>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <Button
                  size="sm"
                  className="px-6 py-2 font-semibold"
                  style={{
                    backgroundColor: storeData.primaryColor,
                    color: "white",
                    border: "none",
                  }}
                >
                  Checkout
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CustomerStore;
